class CustomNewsletterPopup {
  constructor() {
    this.popup = document.getElementById('custom-newsletter-popup');
    if (!this.popup) return;

    this.closeBtn = this.popup.querySelector('.custom__newsletter--popup__close');
    this.form = this.popup.querySelector('.custom-newsletter-form');
    this.overlay = this.popup;
    this.cookieName = 'custom-newsletter-popup-closed';
    
    // Get settings from data attributes
    this.delay = parseInt(this.popup.dataset.delay) || 3;
    this.expireDays = parseInt(this.popup.dataset.expire) || 2;
    this.mode = this.popup.dataset.mode || 'enable';

    this.init();
  }

  init() {
    // Don't show in Shopify theme editor unless in testing mode
    if (window.Shopify && window.Shopify.designMode && this.mode !== 'test') {
      return;
    }

    // Check if popup should be shown
    if (this.shouldShowPopup()) {
      this.setupEventListeners();
      this.showPopupAfterDelay();
    }

    // Setup Shopify theme editor events
    if (window.Shopify && window.Shopify.designMode) {
      this.setupThemeEditorEvents();
    }
  }

  shouldShowPopup() {
    // Always show in testing mode
    if (this.mode === 'test') {
      return true;
    }

    // Don't show if disabled
    if (this.mode === 'disable') {
      return false;
    }

    // Check if popup was already dismissed
    return !this.getCookie(this.cookieName);
  }

  setupEventListeners() {
    // Close button click
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closePopup());
    }

    // Overlay click (outside popup)
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.closePopup();
      }
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('popup-open')) {
        this.closePopup();
      }
    });

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    }

    // Email input validation
    const emailInput = this.popup.querySelector('.custom__newsletter--popup__input');
    if (emailInput) {
      emailInput.addEventListener('input', (e) => this.validateEmailInput(e.target));
      emailInput.addEventListener('blur', (e) => this.validateEmailInput(e.target));
    }
  }

  setupThemeEditorEvents() {
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.querySelector('#custom-newsletter-popup')) {
        this.showPopup();
      }
    });

    document.addEventListener('shopify:section:select', (event) => {
      if (event.target.querySelector('#custom-newsletter-popup')) {
        this.showPopup();
      }
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target.querySelector('#custom-newsletter-popup')) {
        this.hidePopup();
      }
    });
  }

  showPopupAfterDelay() {
    setTimeout(() => {
      this.showPopup();
    }, this.delay * 1000);
  }

  showPopup() {
    if (this.popup) {
      document.body.classList.add('newsletter-popup-open');
      this.popup.classList.add('popup-open');
      this.popup.style.display = 'flex';
      
      // Focus management for accessibility
      const firstInput = this.popup.querySelector('.custom__newsletter--popup__input');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 100);
      }
    }
  }

  closePopup() {
    if (this.popup) {
      this.popup.classList.add('popup-closing');
      
      setTimeout(() => {
        this.hidePopup();
        
        // Set cookie to not show again for specified days (only in enable mode)
        if (this.mode === 'enable') {
          this.setCookie(this.cookieName, this.expireDays);
        }
      }, 300);
    }
  }

  hidePopup() {
    if (this.popup) {
      this.popup.classList.remove('popup-open', 'popup-closing');
      this.popup.style.display = 'none';
      document.body.classList.remove('newsletter-popup-open');
    }
  }

  async handleFormSubmit(e) {
    e.preventDefault();
    
    const formData = new FormData(this.form);
    const email = formData.get('contact[email]');
    
    if (!this.isValidEmail(email)) {
      this.showError('Please enter a valid email address');
      return;
    }

    this.showLoading();

    try {
      const response = await fetch('/contact', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json, text/javascript, */*; q=0.01',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      // Shopify contact form returns HTML, not JSON
      const responseText = await response.text();
      
      if (response.ok && !responseText.includes('form__message--error')) {
        this.showSuccess();
        // Close popup after successful subscription
        setTimeout(() => {
          this.closePopup();
        }, 3000);
      } else {
        // Try to extract error message from response
        const parser = new DOMParser();
        const doc = parser.parseFromString(responseText, 'text/html');
        const errorElement = doc.querySelector('.form__message--error, .errors');
        const errorMessage = errorElement ? errorElement.textContent.trim() : 'Something went wrong. Please try again.';
        
        this.showError(errorMessage);
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      this.showError('Something went wrong. Please try again.');
    }
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  validateEmailInput(input) {
    const email = input.value.trim();
    const validationError = this.popup.querySelector('.custom__newsletter--popup__validation-error');
    const validationSuccess = this.popup.querySelector('.custom__newsletter--popup__validation-success');
    
    // Remove existing validation classes
    input.classList.remove('custom__newsletter--popup__input-error', 'custom__newsletter--popup__input-success');
    
    // Hide validation messages
    if (validationError) validationError.style.display = 'none';
    if (validationSuccess) validationSuccess.style.display = 'none';

    if (email === '') {
      return; // Don't validate empty input
    }

    if (this.isValidEmail(email)) {
      input.classList.add('custom__newsletter--popup__input-success');
      if (validationSuccess) {
        validationSuccess.textContent = '✓ Valid email address';
        validationSuccess.style.display = 'block';
      }
    } else {
      input.classList.add('custom__newsletter--popup__input-error');
      if (validationError) {
        validationError.textContent = '✗ Please enter a valid email address';
        validationError.style.display = 'block';
      }
    }
  }

  showLoading() {
    const button = this.form.querySelector('.custom__newsletter--popup__button');
    const originalText = button.textContent;
    
    button.disabled = true;
    button.textContent = 'Subscribing...';
    
    // Store original text to restore later
    button.dataset.originalText = originalText;
  }

  showSuccess() {
    const successEl = document.getElementById('newsletter-success');
    const errorEl = document.getElementById('newsletter-error');
    const button = this.form.querySelector('.custom__newsletter--popup__button');
    
    if (successEl) {
      successEl.style.display = 'block';
    }
    
    if (errorEl) {
      errorEl.style.display = 'none';
    }

    // Reset button
    if (button) {
      button.disabled = false;
      button.textContent = button.dataset.originalText || 'Submit';
    }

    // Hide form
    const inputWrapper = this.form.querySelector('.custom__newsletter--popup__input-wrapper');
    if (inputWrapper) {
      inputWrapper.style.display = 'none';
    }
  }

  showError(message) {
    const errorEl = document.getElementById('newsletter-error');
    const successEl = document.getElementById('newsletter-success');
    const button = this.form.querySelector('.custom__newsletter--popup__button');
    
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    }
    
    if (successEl) {
      successEl.style.display = 'none';
    }

    // Reset button
    if (button) {
      button.disabled = false;
      button.textContent = button.dataset.originalText || 'Submit';
    }
  }

  getCookie(name) {
    const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
    return match ? match[2] : null;
  }

  setCookie(name, days) {
    const maxAge = days * 24 * 60 * 60; // Convert days to seconds
    document.cookie = `${name}=true; max-age=${maxAge}; path=/; SameSite=Lax`;
  }

  removeCookie(name) {
    document.cookie = `${name}=; max-age=0; path=/`;
  }
}

// Initialize popup when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new CustomNewsletterPopup();
});

// Handle page navigation for SPAs
if (typeof window !== 'undefined') {
  let popup = null;
  
  const initPopup = () => {
    if (!popup) {
      popup = new CustomNewsletterPopup();
    }
  };

  // For Shopify's AJAX cart and other dynamic content
  document.addEventListener('shopify:section:load', initPopup);
  
  // For theme editor
  if (window.Shopify && window.Shopify.designMode) {
    initPopup();
  }
}